import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { eplusHub } from './api/eplus';
import type { CurrentStoreProps, EplusApps } from './SideBar';
import { extractHubSessionForNotifications } from './hub/extractHubSessionForNotifications';
// import testLog from '../../testLog.json';
export type User = {
    name: string;
    imagem_perfil?: string | null;
    chave_empreender: string;
    lang: string;
    loading?: boolean;
    current_store: CurrentStoreProps;
    apps: EplusApps;
    fixed_apps_fast_login_bar?: string[];
    plus_token?: string | null;
    empreender_plus_user_id?: string | number | null;
};

type UserContextType = {
    user: User | null;
    hideApps?: boolean;
    iframe?: boolean;
};

const UserContext = createContext<UserContextType | null>(null);

interface UserProviderProps {
    children: ReactNode;
    appEndPoint?: string;
    headers?: Record<string, string>;
    hideApps?: boolean;
    iframe?: boolean;
}

export function UserProvider({ children, appEndPoint, headers, hideApps, iframe = false }: UserProviderProps) {
    const [user, setUser] = useState<User | null>({ loading: true } as User);

    useEffect(() => {
        if (!appEndPoint) return;

        const options = {
            method: 'POST',
            credentials: 'include' as RequestCredentials,
            headers,
            body: JSON.stringify({
                acao: 'buscarUsuarioComConfigs',
            }),
        };

        eplusHub(options, appEndPoint).then((res) => {
            const { name, current_store, imagem_perfil, lang, apps, fixed_apps_fast_login_bar } = res?.data || {};

            // setUser({ ...testLog.data, loading: false });

            if (res === undefined) return;

            const hubSession = extractHubSessionForNotifications(res?.data);

            setUser({
                name: name || '',
                imagem_perfil: imagem_perfil || null,
                chave_empreender: current_store?.chave_empreender || '',
                lang,
                loading: false,
                current_store: current_store || { link_loja: '', nome_plano: '' },
                apps: apps || {},
                ...(hubSession.plus_token ? { plus_token: hubSession.plus_token } : {}),
                ...(hubSession.empreender_plus_user_id != null ? { empreender_plus_user_id: hubSession.empreender_plus_user_id } : {}),
                ...(Array.isArray(fixed_apps_fast_login_bar)
                    ? {
                          fixed_apps_fast_login_bar: fixed_apps_fast_login_bar.map((key: string) => String(key).toUpperCase()),
                      }
                    : {}),
            });
        });

        /**
         * INICIO: Método legado da barra antiga do plus para ter compatibilidade com outros apps quando a outra barra for removida
         */
        const verifyIfLoggedinBeforeRedirect = (link: string, blank = false) => {
            const redirectOptions = {
                method: 'POST',
                credentials: 'include' as RequestCredentials,
                headers,
                body: JSON.stringify({
                    acao: 'logarConta',
                    next_link: link,
                }),
            };

            eplusHub(redirectOptions, appEndPoint).then((res) => {
                try {
                    const tracker = (window as unknown as { EmpreenderTrack?: (event: string, data?: unknown) => void }).EmpreenderTrack;
                    tracker?.('login', { method: 'Empreender+Banner_auth_redirect' });
                } catch (err) {
                    console.log('error EmpreenderTrack', err);
                }

                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                let shouldOpenBlank = blank;
                let timeoutMs = 250;

                if (isSafari) {
                    shouldOpenBlank = false;
                }

                if (shouldOpenBlank) {
                    timeoutMs = 10;
                }

                setTimeout(() => {
                    const destination = res?.data?.link_magico_redirecionar ?? link;
                    if (shouldOpenBlank) {
                        try {
                            window.open(destination, '_blank');
                        } catch (err) {
                            console.log(err);
                        }
                    } else {
                        window.location.href = destination;
                    }
                }, timeoutMs);
            });
        };

        const apiMethodsWindow = window as unknown as {
            empreenderPlusApiMethods?: Record<string, unknown>;
        };
        apiMethodsWindow.empreenderPlusApiMethods = {
            ...(apiMethodsWindow.empreenderPlusApiMethods || {}),
            verifyIfLoggedinBeforeRedirect,
        };
        /**
         * FIM: Método legado da barra antiga do plus para ter compatibilidade com outros apps quando a outra barra for removida
         */
    }, [appEndPoint]);

    return <UserContext.Provider value={{ user, hideApps, iframe }}>{children}</UserContext.Provider>;
}

export function useUser() {
    const context = useContext(UserContext);
    if (!context) throw new Error('useUser must be used within UserProvider');
    return context;
}
